import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { registrationService, UserRegistrationRequest } from '../services/registration-service';
import { DivisionRole } from '../types';

export function RegisterDivisionPage() {
  const navigate = useNavigate();
  const { slug } = useParams<{ slug: string }>();

  const [divisionName, setDivisionName] = useState<string | null>(null);
  const [divisionRoles, setDivisionRoles] = useState<DivisionRole[]>([]);
  const [loadingDivision, setLoadingDivision] = useState(true);
  const [loadingRoles, setLoadingRoles] = useState(false);
  const [notFound, setNotFound] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string>('');
  const [success, setSuccess] = useState(false);

  const [formData, setFormData] = useState({
    email: '',
    firstName: '',
    lastName: '',
    divisionRoleId: '',
    dodaac: '',
    divOrg: '',
    shopOffice: '',
  });

  useEffect(() => {
    if (!slug) {
      setNotFound(true);
      setLoadingDivision(false);
      return;
    }

    let cancelled = false;

    const load = async () => {
      setLoadingDivision(true);
      setLoadingRoles(true);
      setError('');
      try {
        const division = await registrationService.getDivisionBySlug(slug);
        if (cancelled) return;
        setDivisionName(division.divisionName);

        const roles = await registrationService.getDivisionRolesBySlug(slug);
        if (cancelled) return;
        setDivisionRoles(roles);
      } catch (err) {
        if (cancelled) return;
        console.error('Failed to load registration link:', err);
        setNotFound(true);
      } finally {
        if (!cancelled) {
          setLoadingDivision(false);
          setLoadingRoles(false);
        }
      }
    };

    load();

    return () => {
      cancelled = true;
    };
  }, [slug]);

  const uppercaseFields = ['dodaac', 'divOrg', 'shopOffice'];

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    const nextValue = uppercaseFields.includes(name) ? value.toUpperCase() : value;
    setFormData(prev => ({ ...prev, [name]: nextValue }));
    setError('');
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setSuccess(false);

    if (!slug) return;

    if (!formData.email || !formData.firstName || !formData.lastName ||
        !formData.divisionRoleId || !formData.dodaac || !formData.divOrg || !formData.shopOffice) {
      setError('Please fill in all required fields');
      return;
    }

    setLoading(true);

    try {
      const organizationIdentifier = [formData.dodaac, formData.divOrg, formData.shopOffice].join('|');

      const request: UserRegistrationRequest = {
        email: formData.email,
        firstName: formData.firstName,
        lastName: formData.lastName,
        divisionRoleId: formData.divisionRoleId,
        organizationIdentifier,
      };

      const response = await registrationService.registerUserBySlug(slug, request);

      setSuccess(true);
      setFormData({
        email: '',
        firstName: '',
        lastName: '',
        divisionRoleId: '',
        dodaac: '',
        divOrg: '',
        shopOffice: '',
      });

      alert(`Registration successful!\n\n${response.message}\n\nYou can now sign in with your email address.`);

    } catch (err: any) {
      console.error('Registration failed:', err);
      const errorMessage = err.response?.data?.message || err.message || 'Registration failed. Please try again.';
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  if (loadingDivision) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-primary-50 to-secondary-50 flex items-center justify-center p-4">
        <div className="bg-white dark:bg-secondary-800 rounded-lg shadow-lg p-8 max-w-md w-full text-center text-secondary-600 dark:text-secondary-400">
          Loading…
        </div>
      </div>
    );
  }

  if (notFound) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-primary-50 to-secondary-50 flex items-center justify-center p-4">
        <div className="bg-white dark:bg-secondary-800 rounded-lg shadow-lg p-8 max-w-md w-full">
          <h1 className="text-2xl font-bold text-secondary-900 dark:text-white mb-4">Registration Link Not Found</h1>
          <p className="text-secondary-600 dark:text-secondary-400 mb-6">
            This registration link is invalid or is no longer active. Please contact your administrator for a
            current link.
          </p>
          <Button onClick={() => navigate('/')} variant="secondary" className="w-full">
            Return to Home
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-primary-50 to-secondary-50 flex items-center justify-center p-4">
      <div className="bg-white dark:bg-secondary-800 rounded-lg shadow-lg p-8 max-w-2xl w-full">
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-secondary-900 dark:text-white mb-2">Register / Update Your Account</h1>
          {divisionName && (
            <p className="text-lg font-semibold text-primary-600 dark:text-primary-400 mb-2">
              {divisionName}
            </p>
          )}
          <p className="text-secondary-600 dark:text-secondary-400">
            New users: create an account below. Already registered? Submitting this form again with a new
            organization identifier adds that workflow to your existing account — no duplicate account is created.
          </p>
        </div>

        {success && (
          <div className="mb-6 bg-green-50 dark:bg-green-950 border border-green-200 dark:border-green-800 rounded-lg p-4">
            <p className="text-green-800 font-medium">Registration successful!</p>
            <p className="text-green-700 dark:text-green-400 text-sm mt-1">
              Check your email for further instructions.
            </p>
          </div>
        )}

        {error && (
          <div role="alert" className="mb-6 bg-red-50 dark:bg-red-950 border border-red-200 dark:border-red-800 rounded-lg p-4">
            <p className="text-red-800">{error}</p>
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Email */}
          <div>
            <label htmlFor="email" className="block text-sm font-medium text-secondary-700 dark:text-secondary-200 mb-2">
              Email Address <span className="text-red-500">*</span>
            </label>
            <input
              type="email"
              id="email"
              name="email"
              value={formData.email}
              onChange={handleChange}
              className="w-full px-4 py-2 border border-secondary-300 dark:border-secondary-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent bg-white dark:bg-secondary-900 text-secondary-900 dark:text-secondary-100"
              placeholder="you@example.com"
              required
            />
          </div>

          {/* First Name */}
          <div>
            <label htmlFor="firstName" className="block text-sm font-medium text-secondary-700 dark:text-secondary-200 mb-2">
              First Name <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              id="firstName"
              name="firstName"
              value={formData.firstName}
              onChange={handleChange}
              className="w-full px-4 py-2 border border-secondary-300 dark:border-secondary-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent bg-white dark:bg-secondary-900 text-secondary-900 dark:text-secondary-100"
              placeholder="John"
              required
            />
          </div>

          {/* Last Name */}
          <div>
            <label htmlFor="lastName" className="block text-sm font-medium text-secondary-700 dark:text-secondary-200 mb-2">
              Last Name <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              id="lastName"
              name="lastName"
              value={formData.lastName}
              onChange={handleChange}
              className="w-full px-4 py-2 border border-secondary-300 dark:border-secondary-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent bg-white dark:bg-secondary-900 text-secondary-900 dark:text-secondary-100"
              placeholder="Doe"
              required
            />
          </div>

          {/* Division Role */}
          <div>
            <label htmlFor="divisionRoleId" className="block text-sm font-medium text-secondary-700 dark:text-secondary-200 mb-2">
              Role <span className="text-red-500">*</span>
            </label>
            <select
              id="divisionRoleId"
              name="divisionRoleId"
              value={formData.divisionRoleId}
              onChange={handleChange}
              className="w-full px-4 py-2 border border-secondary-300 dark:border-secondary-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent bg-white dark:bg-secondary-900 text-secondary-900 dark:text-secondary-100"
              disabled={loadingRoles}
              required
            >
              <option value="">
                {loadingRoles ? 'Loading roles...' : 'Select a role...'}
              </option>
              {divisionRoles.map(role => (
                <option key={role.id} value={role.id}>
                  {role.name}
                </option>
              ))}
            </select>
          </div>

          {/* Organization Identifier */}
          <div>
            <span className="block text-sm font-medium text-secondary-700 dark:text-secondary-200 mb-2">
              Organization Identifier <span className="text-red-500">*</span>
            </span>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <div>
                <label htmlFor="dodaac" className="block text-xs font-medium text-secondary-600 dark:text-secondary-300 mb-1">
                  DODAAC
                </label>
                <input
                  type="text"
                  id="dodaac"
                  name="dodaac"
                  value={formData.dodaac}
                  onChange={handleChange}
                  className="w-full px-4 py-2 border border-secondary-300 dark:border-secondary-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent bg-white dark:bg-secondary-900 text-secondary-900 dark:text-secondary-100 uppercase"
                  placeholder="DODAAC"
                  required
                />
              </div>
              <div>
                <label htmlFor="divOrg" className="block text-xs font-medium text-secondary-600 dark:text-secondary-300 mb-1">
                  Division/Org
                </label>
                <input
                  type="text"
                  id="divOrg"
                  name="divOrg"
                  value={formData.divOrg}
                  onChange={handleChange}
                  className="w-full px-4 py-2 border border-secondary-300 dark:border-secondary-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent bg-white dark:bg-secondary-900 text-secondary-900 dark:text-secondary-100 uppercase"
                  placeholder="Division/Org"
                  required
                />
              </div>
              <div>
                <label htmlFor="shopOffice" className="block text-xs font-medium text-secondary-600 dark:text-secondary-300 mb-1">
                  Shop/Office
                </label>
                <input
                  type="text"
                  id="shopOffice"
                  name="shopOffice"
                  value={formData.shopOffice}
                  onChange={handleChange}
                  className="w-full px-4 py-2 border border-secondary-300 dark:border-secondary-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent bg-white dark:bg-secondary-900 text-secondary-900 dark:text-secondary-100 uppercase"
                  placeholder="Shop/Office"
                  required
                />
              </div>
            </div>
            <p className="mt-1 text-sm text-secondary-500 dark:text-secondary-400">
              Enter your DODAAC, Division/Organization, and Shop/Office identifiers
            </p>
          </div>

          {/* Submit Button */}
          <div className="flex gap-4">
            <Button
              type="submit"
              disabled={loading}
              className="flex-1"
            >
              {loading ? 'Submitting...' : 'Submit'}
            </Button>
            <Button
              type="button"
              variant="secondary"
              onClick={() => navigate('/')}
              className="flex-1"
            >
              Cancel
            </Button>
          </div>
        </form>

        <div className="mt-6 text-center text-sm text-secondary-600 dark:text-secondary-400">
          Already have an account?{' '}
          <button
            onClick={() => navigate('/')}
            className="text-primary-600 hover:text-primary-700 font-medium"
          >
            Sign in
          </button>
        </div>
      </div>
    </div>
  );
}
